import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import { downloadFile } from "common/helpers/utils";
import Guid from "common/values/guid/guid";
import DocumentAPIService from "documents/entities/document/api/document-api-service";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useSession } from "users/session/session-context";
import WorkAgreement, { Masked, WorkAgreementFieldCategory } from "work/entities/work-agreement/work-agreement";
import DocumentListReadonly from "work/values/work-document/view/document-list-readonly";
import {
  WorkDocumentType,
} from "work/values/work-document/work-document";

const ListContainer = styled(Grid)(
  () => ({
    flexWrap: "nowrap",
    minHeight: "20rem",
    width: "100%",
  }));
const DocumentList = styled(DocumentListReadonly)(
  ({ theme }) => ({
    [theme.breakpoints.down("lg")]: {
      marginTop: theme.spacing(2),
    },
    maxHeight: "60vh",
    marginLeft: theme.spacing(2),
    overflowY: "auto",
  }));
const HelperText = styled(Typography)(
  ({ theme }) => ({
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  }));

type PoliciesTabProps = {
  activeTab: WorkAgreementFieldCategory;
  workAgreement: WorkAgreement;
};

export default function PoliciesTab(props: Readonly<PoliciesTabProps>) {
  const {
    activeTab,
    workAgreement
  } = props;

  const [isDownloadingFile, setIsDownloadingFile] = React.useState<Guid | null>(null);

  const session = useSession();

  async function handleDownloadDocumentById(
    documentId: Guid,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): Promise<void> {
    event?.stopPropagation();
    try {
      setIsDownloadingFile(documentId);
      const documentApiService = new DocumentAPIService(session);
      const policyDocument = await documentApiService.downloadDocument(
        documentId
      );
      await downloadFile(policyDocument);
    } catch (err) {
      console.error(err);
      enqueueSnackbar(
        "Failed to download the document. Please try again later.",
        {
          variant: "error",
        }
      );
    } finally {
      setIsDownloadingFile(null);
    }
  }

  if (activeTab !== WorkAgreementFieldCategory.Policies) return null;
  if (Masked.isMasked(workAgreement.vendorPolicyDocuments) ||
    Masked.isMasked(workAgreement.clientPolicyDocuments)) {
    return "You do not have permission to view the policy documents.";
  }

  const selectedDocuments = session.context?.viewingAsVendor
    ? workAgreement.vendorPolicyDocuments
    : workAgreement.clientPolicyDocuments;

  return (
    <ListContainer container direction="row">
      <Grid size={4}>
        {selectedDocuments.length === 0 &&
          <HelperText>
            No policy documents for this work agreement.
          </HelperText>
        }{selectedDocuments.length > 0 &&
          <DocumentList
            documents={selectedDocuments}
            documentType={
              session.context?.viewingAsVendor
                ? WorkDocumentType.VendorPolicy
                : WorkDocumentType.ClientPolicy
            }
            downloadingFileId={isDownloadingFile}
            onDownload={handleDownloadDocumentById}
          />
        }
      </Grid>
    </ListContainer>
  );
}
